import type { EffectRef, Signal } from '@angular/core';
import { computed, effect, signal, untracked } from '@angular/core';
import type { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { form, readonly } from '@angular/forms/signals';

import { createFormTransaction } from '../plugins/form-transaction';
import { deepPatch } from './deep-patch';

/**
 * `parentId` is optional: a block sits directly on the page and carries no
 * parent link, and it is the only entity in the package that does. Every other
 * one has it, so the accessor below stays — it just reads `undefined` there.
 */
type BaseEntity = {
    id: string;
    parentId?: string;
    entityType: string;
    entitySubType: string;
    version: number;
    props: any;
};

export type BaseEntityForm<T extends BaseEntity> = {
    _raw: FieldTree<T>;
    syncFromEntity: (entity: T) => void;
    destroy: () => void;
    /**
     * Ask the host to remove this entity. The optional `payload` is forwarded
     * verbatim to {@link BaseEntityFormOptions.onRemove} — placement context the
     * form doesn't own travels through here from the caller.
     */
    remove: (payload?: unknown) => void;
    /** Ask the host to copy this entity; see {@link BaseEntityForm.remove} for the payload contract. */
    copy: (payload?: unknown) => void;
    inTransaction: Signal<boolean>;
    beginTransaction: () => void;
    commitTransaction: () => void;
    rollbackTransaction: () => void;
    transact: (fn: () => void) => void;
    /**
     * Same semantics as {@link BaseEntityForm.transact}, but the `onUpdate`
     * emission produced by the commit carries `{ skipHistory: true }` — hosts use
     * it to persist the change without recording a history entry. Caveat:
     * synchronous writes coalesce into one `onUpdate` per tick, so any plain
     * writes landing in the same tick are tagged skip-history too.
     */
    transactWithoutHistory: (fn: () => void) => void;
    value: () => T;
    setValue: (value: T) => void;
    id: Signal<string>;
    parentId: Signal<string | undefined>;
};

export interface BaseEntityFormOptions<T extends BaseEntity> {
    entity?: Signal<any>;
    /**
     * Synchronous snapshot of the entity at form-construction time, for factories
     * that pick a validation schema or accessor set before the `entity` signal
     * has populated. Only inspected at construction, never reactively.
     */
    initialData?: T;
    onUpdate?: (value: T, meta: { skipHistory: boolean }) => void;
    onCreate?: (ctx: { form: BaseEntityForm<T> }) => void;
    onDestroy?: () => void;
    onRemove?: (payload?: unknown) => void;
    onCopy?: (payload?: unknown) => void;
}

/**
 * Marks the infrastructure fields every versioned entity shares (`id`,
 * `entityType`, `entitySubType`, `version`) as `readonly` on the schema path —
 * the versioned twin of `configureCommonReadonly`. Called from each versioned
 * factory's schema configurator.
 */
export function configureVersionedReadonly(schemaPath: any): void {
    readonly(schemaPath.id);
    readonly(schemaPath.entityType);
    readonly(schemaPath.entitySubType);
    readonly(schemaPath.version);
}

export function createBaseEntityForm<T extends BaseEntity>(
    configureSchema: (schemaPath: SchemaPathTree<T>) => void,
    options?: BaseEntityFormOptions<T>
) {
    const model = signal<T>({ props: null } as any);
    let entitySyncVersion = 0;
    let lastSeenSyncVersion = 0;
    let skipHistoryOnNextUpdate = false;

    const _raw = form(model, configureSchema);

    const _tx = createFormTransaction(model);

    const effectRefs: EffectRef[] = [];

    if (options?.entity) {
        // Sync with the entity signal whenever it changes. We use an effect here to automatically handle cleanup when the form is destroyed.
        effectRefs.push(
            effect(() => {
                const entityValue = options.entity!();
                untracked(() => {
                    const prev = model();
                    model.update((current) => deepPatch(current, entityValue));
                    if (model() !== prev) {
                        entitySyncVersion++;
                    }
                });
            })
        );
    }

    if (options?.onUpdate) {
        effectRefs.push(
            effect(() => {
                const value = model();
                const inTx = _tx.inTransaction();

                if (entitySyncVersion !== lastSeenSyncVersion) {
                    lastSeenSyncVersion = entitySyncVersion;
                    return;
                }
                if (inTx) {
                    return;
                }
                if (!value.props) {
                    return;
                }

                untracked(() => {
                    const skipHistory = skipHistoryOnNextUpdate;
                    skipHistoryOnNextUpdate = false;
                    // A copy, so the host's store never freezes the model's own objects — see the legacy twin (#75636).
                    options.onUpdate!(structuredClone(value), { skipHistory });
                });
            })
        );
    }

    const result: BaseEntityForm<T> = {
        _raw,

        syncFromEntity: (entity: T) => {
            const prev = model();
            model.update((current) => deepPatch(current, entity));
            if (model() !== prev) {
                entitySyncVersion++;
            }
        },

        destroy: () => {
            options?.onDestroy?.();
            for (const ref of effectRefs) {
                ref.destroy();
            }
        },

        remove: (payload?: unknown) => {
            options?.onRemove?.(payload);
        },

        copy: (payload?: unknown) => {
            options?.onCopy?.(payload);
        },

        inTransaction: _tx.inTransaction,
        beginTransaction: () => _tx.beginTransaction(),
        commitTransaction: () => _tx.commitTransaction(),
        rollbackTransaction: () => {
            // Only suppress the rollback's reverting emission (via the version
            // guard) when there is actually an open transaction to roll back.
            // Bumping unconditionally would arm the guard against the *next*
            // genuine user edit and silently drop it. See `_tx.rollbackTransaction`,
            // which no-ops (warns) when no transaction is active.
            if (_tx.inTransaction()) {
                entitySyncVersion++;
            }
            _tx.rollbackTransaction();
        },
        transact: (fn: () => void) => _tx.transact(fn),
        transactWithoutHistory: (fn: () => void) => {
            const before = untracked(model);
            // Only the outermost frame produces the emission this flag is meant to
            // tag; nested inside another transaction the emission belongs to that
            // caller's user action.
            const ownsEmission = !_tx.inTransaction();
            try {
                _tx.transact(fn);
            } finally {
                // Arm only when the model actually changed, so the flag can't leak
                // onto an unrelated later update.
                if (ownsEmission && untracked(model) !== before) {
                    skipHistoryOnNextUpdate = true;
                }
            }
        },

        value: () => _raw().value() as T,
        setValue: (value: T) => _raw().value.set(value as any),

        id: computed(() => (_raw().value() as T).id),
        parentId: computed(() => (_raw().value() as T).parentId),
    };

    options?.onCreate?.({ form: result });

    return result;
}
